/**
 * D04 — Support a Meal: which meal.
 *
 * The artwork put meal choice and headcount on one screen. On a small phone
 * the headcount then sat below the whole menu, so the question that decides
 * the price was the one a donor had to scroll to find. It is its own screen
 * now (`PeopleCountScreen`), and this one only asks which meal.
 */

import { StyleSheet, View } from 'react-native';

import { formatMoney } from '@mfa/domain';
import { colors, radius, sizing, space } from '../../theme';
import { Button } from '../../components/common/Button';
import { ArrowIcon } from '../../components/common/Icon';
import { EmptyState, ErrorState } from '../../components/feedback/States';
import { Glyph } from '../../components/common/Icon';
import { MealCarousel } from '../../components/donation/MealCarousel';
import { MealList } from '../../components/donation/MealList';
import { useMealLayout } from './useMealLayout';
import { HeaderPill } from '../../components/layout/HeaderPill';
import { ScreenShell } from '../../components/layout/ScreenShell';
import { ScreenTitle } from '../../components/layout/ScreenTitle';
import { Text } from '../../components/common/Text';
import { useMeals } from '../../core/api/hooks';
import { useDonationStore } from './donationStore';
import type { DonorScreenProps } from '../../app/navigation/types';
import { useT } from '../../core/i18n/LanguageProvider';
import { useLocalized } from '../../core/i18n/content';

export function DonateMealScreen({ navigation }: DonorScreenProps<'DonateMeal'>) {
  const t = useT();
  const pick = useLocalized();
  const meals = useMeals();
  const [layout, toggleLayout] = useMealLayout();

  const meal = useDonationStore((s) => s.meal);
  const setMeal = useDonationStore((s) => s.setMeal);

  if (meals.isError) {
    return (
      <ScreenShell leading="back" onBackPress={navigation.goBack} testID="screen-donate-meal">
        <ErrorState onRetry={() => void meals.refetch()} />
      </ScreenShell>
    );
  }

  const available = meals.data ?? [];

  return (
    <ScreenShell
      leading="back"
      onBackPress={navigation.goBack}
      /* In the nav bar rather than beside the title: it acts on the list
         below, and the bar is where this app puts a screen's one action.
         Switching arrangement must not disturb the selection or refetch —
         both live above this component. */
      trailing={
        <HeaderPill
          onPress={toggleLayout}
          accessibilityLabel={
            layout === 'list' ? t('Switch to card layout') : t('Switch to list layout')
          }
          selected={layout === 'carousel'}
          testID="meal-layout-toggle"
        >
          <Glyph name={layout === 'list' ? 'grid' : 'list'} size={sizing.icon.md} />
        </HeaderPill>
      }
      testID="screen-donate-meal"
      pinnedFooter={
        <View style={styles.pinned}>
          <View style={styles.summaryRow}>
            <View style={styles.summaryText}>
              <Text variant="caption" tone="tertiary">{t('You selected')}</Text>
              <Text variant="cardTitle" numberOfLines={1}>
                {meal ? pick(meal.name, meal.nameUr) : t('Choose a meal')}
              </Text>
            </View>

            {meal ? (
              <View style={styles.totalBlock}>
                <Text variant="caption" tone="tertiary" align="right">{t('Price Per Person')}</Text>
                <Text variant="amount" tone="brand" align="right">
                  {formatMoney(meal.unitPrice)}
                </Text>
              </View>
            ) : null}
          </View>

          <Button
            label={t('Continue')}
            onPress={() => navigation.navigate('PeopleCount')}
            disabled={!meal}
            trailingIcon={<ArrowIcon />}
            testID="cta-meal-continue"
          />
        </View>
      }
      subHeader={
        /* The same light-green card Home's hero uses, so the screen a donor
           arrives at from it reads as the same product. It is pinned, so the
           meals scroll under it rather than taking the title with them. */
        <ScreenTitle
          title={t('Choose a Meal to Support')}
          subtitle={t('Nutritious meals prepared with love and care')}
        />
      }
    >
      {!meals.isLoading && available.length === 0 ? (
        <EmptyState
          title={t('No meals available right now')}
          message={t('Please check back shortly — we are updating our menu.')}
          actionLabel={t('Refresh')}
          onAction={() => void meals.refetch()}
        />
      ) : layout === 'list' ? (
        <MealList
          meals={available}
          selectedId={meal?.id ?? null}
          onSelect={setMeal}
          loading={meals.isLoading}
        />
      ) : (
        <MealCarousel
          meals={available}
          selectedId={meal?.id ?? null}
          onSelect={setMeal}
          loading={meals.isLoading}
        />
      )}
    </ScreenShell>
  );
}

const styles = StyleSheet.create({
  pinned: {
    gap: space.md,
  },
  summaryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.md,
    backgroundColor: colors.bg.surface,
    borderRadius: radius.lg,
    padding: space.md,
  },
  summaryText: {
    flex: 1,
    gap: space.xxs,
  },
  totalBlock: {
    alignItems: 'flex-end',
    gap: space.xxs,
  },
});
